import React, { useState, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CountrySelector({ value, onChange, compact = false }) {
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFrameworks();
  }, []);

  const fetchFrameworks = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frameworks`);
      const data = await res.json();
      setFrameworks(data.frameworks || []);
    } catch (err) {
      console.error('Failed to fetch frameworks:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedFramework = frameworks.find(f => f.country_code === value);

  if (loading) {
    return (
      <Button variant="outline" disabled className="w-full justify-between bg-slate-900 border-slate-700">
        <span className="text-slate-400">Loading...</span>
      </Button>
    );
  }

  if (compact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 gap-1 text-slate-300 hover:text-white">
            <span className="text-lg">{selectedFramework?.flag_emoji || '🌐'}</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
          {frameworks.map((fw) => (
            <DropdownMenuItem
              key={fw.country_code}
              onClick={() => onChange(fw.country_code)}
              className={`cursor-pointer ${
                value === fw.country_code
                  ? 'text-amber-500 bg-amber-500/10'
                  : 'text-slate-300 hover:text-white hover:bg-slate-800'
              }`}
            >
              <span className="text-lg mr-2">{fw.flag_emoji}</span>
              {fw.country_name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="w-full justify-between bg-slate-900 border-slate-700 hover:bg-slate-800 hover:border-slate-600"
        >
          <div className="flex items-center gap-2">
            {selectedFramework ? (
              <>
                <span className="text-lg">{selectedFramework.flag_emoji}</span>
                <span className="text-white">{selectedFramework.country_name}</span>
              </>
            ) : (
              <>
                <Globe className="h-4 w-4 text-slate-400" />
                <span className="text-slate-400">Select country</span>
              </>
            )}
          </div>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-full min-w-[200px] bg-slate-900 border-slate-800">
        {frameworks.map((fw) => (
          <DropdownMenuItem
            key={fw.country_code}
            onClick={() => onChange(fw.country_code)}
            className={`cursor-pointer ${
              value === fw.country_code
                ? 'text-amber-500 bg-amber-500/10'
                : 'text-slate-300 hover:text-white hover:bg-slate-800'
            }`}
          >
            <span className="text-lg mr-2">{fw.flag_emoji}</span>
            <div>
              <div>{fw.country_name}</div>
              <div className="text-xs text-slate-500">{fw.product_count} financing options</div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
